import { Flex, Text } from '@mantine/core';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { formatCurrency } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { useClients } from 'src/clients/hooks/useClients';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useOwnerNames } from 'src/financial/financial-data/hooks/useOwnerNames';
import { recordedValue } from 'src/financial/financial-data/ownerShares';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import {
  OwnersEditor,
  type OwnerRowValue,
  productSupportsOwners,
} from 'src/financial/financial-data/financial-item/forms/common/OwnersEditor';
import { valueFieldFor } from 'src/financial/financial-data/financial-item/rework/financialItemRework';
import {
  DetectionState,
  EntityCategory,
  selectIsDiscarded,
  selectIsJoint,
  selectIsResolved,
  selectShownOwners,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';

type Props = {
  category: EntityCategory;
  detection: DetectionState;
};

// Data Upload Review owners editor: lets advisors correct who a detected item belongs to
// before import. Reuses the create form's controlled OwnersEditor and writes edits back to
// the review store, from where they are forwarded on finalise (see getPayloadForNewDetection).
export function DetectionOwnersEditor({ category, detection }: Props) {
  const { flagEnabledFinancialItemOwners } = useFlags();
  const { clientId } = useCurrentClient();
  const { members } = useFamilyMembers();
  // A conflicting record can already be owned with a client outside the family, who would
  // otherwise read "Unknown owner" and could be removed without the adviser knowing who it was.
  const resolveOwnerName = useOwnerNames(members, [clientId]);
  const { clients } = useClients({}, { enabled: !!flagEnabledFinancialItemOwners });
  const tenantCurrency = useCurrency();
  const setResolvedOwners = useDetectedEntitiesStore(state => state.setResolvedOwners);
  // Add new, Update and discard all save immediately, so owners edited afterwards would never
  // reach the backend. The detected fields lock on the same state.
  const isResolved = useDetectedEntitiesStore(state =>
    selectIsResolved(state, category, detection.id),
  );
  const isDiscarded = useDetectedEntitiesStore(state =>
    selectIsDiscarded(state, category, detection.id),
  );
  const shownOwners: OwnerRowValue[] = useDetectedEntitiesStore(state =>
    selectShownOwners(state, category, detection.id),
  );
  const isJoint = useDetectedEntitiesStore(state => selectIsJoint(state, category, detection.id));
  const setJoint = useDetectedEntitiesStore(state => state.setJoint);

  if (!flagEnabledFinancialItemOwners || !productSupportsOwners(detection.entityType)) {
    return null;
  }

  // An item is only on this client's review because it was detected for them, so they stay one of
  // its owners here. Detections from before owners were detected arrive with none, and start with
  // this client alone.
  const owners = shownOwners.some(owner => owner.client_id === clientId)
    ? shownOwners
    : [{ client_id: clientId, ownership_percentage: null }, ...shownOwners];
  const valueField = valueFieldFor(detection.entityType);
  const valueMetadata = detection.fieldsByKey[valueField]?.metadata;
  const total = recordedValue(detection.fieldsByKey[valueField]?.value);
  // The detection carries its own currency, so an owner's slice reads in the same currency and
  // style as the value shown above it on the card. Tenant currency covers a non-money field.
  const currency = valueMetadata?.type === 'MONEY' ? valueMetadata.currency : tenantCurrency;

  const handleChange = (next: OwnerRowValue[]) => setResolvedOwners(category, detection.id, next);

  return (
    <Flex direction="column" gap="md" data-testid="detection-owners-editor">
      <Text fw={700} size="sm" c="gray.10">
        Owners
      </Text>
      <OwnersEditor
        owners={owners}
        onChange={handleChange}
        members={members}
        resolveOwnerName={resolveOwnerName}
        otherClients={clients}
        total={total}
        formatAmount={amount => formatCurrency(amount, currency) ?? ''}
        showJointToggle={detection.jointDecidedBySwitch === true}
        isJoint={isJoint}
        onJointChange={joint => setJoint(category, detection.id, joint)}
        disabled={isResolved || isDiscarded}
        lockedClientId={clientId}
      />
    </Flex>
  );
}
